/**
 * @typedef {Object} TouchEventData
 * @property {function} setEndEvent - Sets the end event for touch tracking
 * @property {function} isSwipeRight - Checks if the touch was a right swipe
 * @property {function} isSwipeLeft - Checks if the touch was a left swipe
 */

/**
 * Custom element that implements a mobile product carousel
 * @extends HTMLElement
 */
class MobileHomeProdCarousel extends HTMLElement {
  /**
   * Initialize the mobile product carousel
   */
  constructor() {
    super()

    // Carousel state
    this.mobileProdSelectedIndex = 0
    this.mobileProdAutoInterval = null
    this.mobileProdScrollTimeout = null
    this.mobileProdIntervalTime = 20000
    this.mobileProdTitlesScrollLeft = 0
    this.mobileProdScrollLeft = 0
    /** @type {TouchEventData|null} */
    this.mobileProdTouch = null
    this.productDisplayMax = Number(this.dataset.productDisplayMax?.slice(0, 1) || 1)

    // DOM elements
    this.mobileProdTitlesDiv = document.getElementById("mobile-prod-titles-div")
    this.prodTitleEls = document.querySelectorAll(".product-title")
    this.mobileProdScrollDiv = document.getElementById("mobile-prod-scrolldiv")
    this.mobileProdSwipeableElements = document.getElementsByClassName("mobile-prod-slide")

    this.build()
  }

  /**
   * Initialize the component
   */
  build() {
    // Set up event listeners
    const mobileProdSwipeableObject = this.querySelector("#mobile-prod-swipeable-object")
    if (mobileProdSwipeableObject) {
      mobileProdSwipeableObject.addEventListener("touchstart", this.mobileProdTouchstart.bind(this))
      mobileProdSwipeableObject.addEventListener("touchend", this.mobileProdTouchend.bind(this))
    }

    const prevArrow = this.querySelector("#prev-arrow")
    if (prevArrow) {
      prevArrow.addEventListener("click", this.prevMobileProductPress.bind(this))
    }

    const nextArrow = this.querySelector("#next-arrow")
    if (nextArrow) {
      nextArrow.addEventListener("click", this.nextMobileProductPress.bind(this))
    }

    // Add click listeners to product titles
    if (this.prodTitleEls) {
      for (const prodTitleEl of this.prodTitleEls) {
        prodTitleEl.addEventListener("click", this.setMobileProdIndex.bind(this))
      }
    }

    // Initialize carousel
    this.titleLineBreak()
    this.mobileProdUpdateTitles()
    this.setMobileProdAuto()
  }

  /**
   * Handle next button press
   */
  nextMobileProductPress() {
    this.setMobileProdAuto() // Reset auto interval
    this.mobileProdNext()
  }

  /**
   * Handle previous button press
   */
  prevMobileProductPress() {
    this.setMobileProdAuto() // Reset auto interval
    this.mobileProdPrev()
  }

  /**
   * Set the product index based on title click
   * @param {Event} e - Click event
   */
  setMobileProdIndex(e) {
    if (!e.target || !e.target.dataset || !e.target.dataset.index) return

    const index = Number.parseInt(e.target.dataset.index, 10)
    if (isNaN(index)) return

    this.setMobileProdAuto() // Reset auto interval
    this.mobileProdSelectedIndex = index - 1
    this.mobileProdSetScroll()
  }

  /**
   * Move to the next product
   */
  mobileProdNext() {
    if (!this.mobileProdSwipeableElements) return

    if (this.mobileProdSelectedIndex === this.mobileProdSwipeableElements.length - 1) {
      this.mobileProdSelectedIndex = 0
    } else {
      this.mobileProdSelectedIndex++
    }

    this.mobileProdSetScroll()
  }

  /**
   * Move to the previous product
   */
  mobileProdPrev() {
    if (!this.mobileProdSwipeableElements) return

    if (this.mobileProdSelectedIndex === 0) {
      this.mobileProdSelectedIndex = this.mobileProdSwipeableElements.length - 1
    } else {
      this.mobileProdSelectedIndex--
    }

    this.mobileProdSetScroll()
  }

  /**
   * Check if titles need extra margin for line breaks
   */
  titleLineBreak() {
    if (!this.mobileProdTitlesDiv) return

    const productTitles = document.querySelectorAll(".product-title")
    for (const productTitle of productTitles) {
      if (!this.mobileProdTitlesDiv.classList.contains("mb-2") && productTitle.scrollHeight > 32) {
        this.mobileProdTitlesDiv.classList.add("mb-2")
      }
    }
  }

  /**
   * Set scroll position based on selected index
   */
  mobileProdSetScroll() {
    if (!this.mobileProdScrollDiv || !this.mobileProdTitlesDiv) return

    // Calculate title scroll position based on product display max
    const titleScrollPosition =
      this.mobileProdTitlesDiv.clientWidth * Math.floor(this.mobileProdSelectedIndex / this.productDisplayMax)
    this.mobileProdTitlesDiv.scrollLeft = titleScrollPosition

    // Set product scroll position
    const productScrollPosition = this.mobileProdScrollDiv.clientWidth * this.mobileProdSelectedIndex
    this.mobileProdScrollDiv.scrollLeft = productScrollPosition

    // Store scroll positions
    this.mobileProdScrollLeft = this.mobileProdScrollDiv.scrollLeft
    this.mobileProdTitlesScrollLeft = this.mobileProdTitlesDiv.scrollLeft

    // Update UI
    this.titleLineBreak()
    this.mobileProdUpdateTitles()
  }

  /**
   * Set up auto-scrolling interval
   */
  setMobileProdAuto() {
    clearInterval(this.mobileProdAutoInterval)
    this.mobileProdAutoInterval = setInterval(this.mobileProdNext.bind(this), this.mobileProdIntervalTime)
  }

  /**
   * Handle touch start event
   * @param {TouchEvent} e - Touch start event
   */
  mobileProdTouchstart(e) {
    // Assuming TouchEvent is a custom class defined elsewhere
    if (typeof TouchEvent === "function") {
      this.mobileProdTouch = new TouchEvent(e)
    }
  }

  /**
   * Handle touch end event
   * @param {TouchEvent} e - Touch end event
   */
  mobileProdTouchend(e) {
    if (!this.mobileProdTouch) return

    this.mobileProdTouch.setEndEvent(e)

    if (this.mobileProdTouch.isSwipeRight()) {
      this.prevMobileProductPress()
    } else if (this.mobileProdTouch.isSwipeLeft()) {
      this.nextMobileProductPress()
    }

    this.mobileProdTouch = null
  }

  /**
   * Update product titles and button text
   */
  mobileProdUpdateTitles() {
    if (!this.prodTitleEls) return

    const sendBtnText = this.querySelector("#send-button-text")
    const sendBtnAnchor = this.querySelector("#send-button-anchor")

    if (!sendBtnText) return

    for (let i = 0; i < this.prodTitleEls.length; i++) {
      const prodEl = this.prodTitleEls[i]

      if (!prodEl || !prodEl.dataset) continue

      const btnText = prodEl.dataset.mobileBtnTxt

      if (i === this.mobileProdSelectedIndex) {
        // Update selected product
        if (sendBtnAnchor && prodEl.dataset.productUrl) {
          sendBtnAnchor.href = prodEl.dataset.productUrl
        }

        prodEl.classList.add("underline")

        // Set button text based on product type
        const prodTitle = btnText || prodEl.dataset.productTitle || ""

        if (prodTitle.includes("Subscription")) {
          sendBtnText.innerText = `Start ${prodTitle.split(" ")[0]}`
        } else {
          sendBtnText.innerText = `Send ${prodTitle}`
        }
      } else {
        // Update non-selected products
        prodEl.classList.remove("underline")
      }
    }
  }
}

// Register the custom element
customElements.define("mobile-product-carousel", MobileHomeProdCarousel)
